// Thin fetch wrapper with JWT + X-L2-Session handling.
// M2 refactor 2026-04-22: shared-terminal kiosk.
//   L1: user=máy (admin|kiosk) login với password + TOTP L1 (persistent)
//   L2: NV (employees) setup/verify trên máy kiosk; verify mở session 4h (X-L2-Session)
//   Admin CRUD: /admin/shops, /admin/employees, /admin/users (mỗi máy)
import toast from 'react-hot-toast';
import { useAuthStore } from './auth.js';

// M165 (2026-05-15): tránh toast spam khi nhiều request 401 song song cùng fail
// (refresh đã fail 1 lần → các request đang chờ đều rơi vào else branch). Chỉ
// show toast lần đầu trong 3s; sau đó suppress.
let _lastSessionExpiredToast = 0;

const API_BASE = '/api';

async function raw(path, { method = 'GET', body = null, auth = true, headers = {}, withL2 = false, adminTotp = null, keepalive = false } = {}) {
  const store = useAuthStore.getState();
  const finalHeaders = { ...headers };
  if (body !== null && body !== undefined) finalHeaders['Content-Type'] = 'application/json';
  if (auth && store.accessToken) finalHeaders['Authorization'] = `Bearer ${store.accessToken}`;
  if (withL2) {
    const sid = store.getValidL2SessionId();
    if (sid) finalHeaders['X-L2-Session'] = sid;
  }
  // Admin TOTP re-verify (Option C): dùng cho cost-sensitive admin actions.
  // Caller truyền 6-digit code → set header X-Admin-TOTP. Middleware backend
  // verify bằng totp_l1_secret_enc của chính admin user.
  if (adminTotp) {
    finalHeaders['X-Admin-TOTP'] = String(adminTotp).trim();
  }

  const buildInit = () => ({
    method,
    headers: finalHeaders,
    body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
    // M150 (2026-05-13): keepalive=true để fetch tiếp tục gửi sau khi tab đóng
    // (heartbeat exam khi NV bấm đóng tab giữa chừng). Body tối đa 64KB.
    ...(keepalive ? { keepalive: true } : {}),
  });

  let res = await fetch(`${API_BASE}${path}`, buildInit());

  // Auto-refresh on 401 TOKEN_EXPIRED
  if (res.status === 401 && auth) {
    const data = await res.clone().json().catch(() => ({}));
    if (data?.code === 'TOKEN_EXPIRED') {
      const ok = await store.refreshAccess();
      if (ok) {
        finalHeaders['Authorization'] = `Bearer ${useAuthStore.getState().accessToken}`;
        res = await fetch(`${API_BASE}${path}`, buildInit());
      } else {
        // M165 (2026-05-15): toast "Phiên hết hạn" trước khi clear localStorage
        // — không silent drop. Debounce 3s tránh toast spam khi nhiều request
        // song song cùng rơi vào branch này (refresh đã fail).
        const now = Date.now();
        if (now - _lastSessionExpiredToast > 3000) {
          _lastSessionExpiredToast = now;
          toast.error('Phiên đã hết hạn — vui lòng đăng nhập lại', { duration: 2500 });
          // Cho user thấy toast 800ms trước khi clear → tránh blink
          setTimeout(() => { store.logout(); }, 800);
        } else {
          store.logout();
        }
      }
    }
  }
  return res;
}

export async function apiJson(path, opts) {
  const res = await raw(path, opts);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
  return data;
}

export const api = {
  // Health check — no auth
  health:         ()                => apiJson('/health',                { auth: false }),

  // Auth core
  login:          (body)            => apiJson('/auth/login',           { method: 'POST', body, auth: false }),
  logout:         (refresh_token)   => apiJson('/auth/logout',          { method: 'POST', body: { refresh_token }, auth: false }),
  me:             ()                => apiJson('/auth/me', { withL2: true }),
  refresh:        (refresh_token)   => apiJson('/auth/refresh',         { method: 'POST', body: { refresh_token }, auth: false }),
  changePassword: (body)            => apiJson('/auth/password/change', { method: 'POST', body }),

  // L2 (employee) — chỉ máy kiosk dùng
  l2Employees:    ()                 => apiJson('/auth/l2/employees'),
  totpL2Setup:    (employee_id)      => apiJson('/auth/l2/setup',  { method: 'POST', body: { employee_id } }),
  totpL2Enable:   (employee_id, code) => apiJson('/auth/l2/enable', { method: 'POST', body: { employee_id, code } }),
  totpL2Verify:   (employee_id, code) => apiJson('/auth/l2/verify', { method: 'POST', body: { employee_id, code } }),
  l2Logout:       (session_id)       => apiJson('/auth/l2/logout', { method: 'POST', body: { session_id } }),

  // Admin user (máy) management
  adminUsers:          ()      => apiJson('/admin/users'),
  adminCreateUser:     (body)  => apiJson('/admin/users',                           { method: 'POST',  body }),
  adminUpdateUser:     (id, b) => apiJson(`/admin/users/${id}`,                     { method: 'PATCH', body: b }),
  // M144a (2026-05-12): metadata permissions groups cho FE render checkbox grid khi tạo manager
  adminPermissionGroups: ()    => apiJson('/admin/permissions/groups'),
  adminResetPassword:  (id, p) => apiJson(`/admin/users/${id}/reset-password`,      { method: 'POST',  body: { new_password: p } }),
  adminGenerateL1:     (id)    => apiJson(`/admin/users/${id}/totp/l1/generate`,    { method: 'POST' }),
  adminVerifyL1:       (id, secret_base32, code) => apiJson(`/admin/users/${id}/totp/l1/verify`, { method: 'POST', body: { secret_base32, code } }),
  adminUnlock:         (id)    => apiJson(`/admin/users/${id}/unlock`,              { method: 'POST' }),

  // M2R.6++ Hard delete user/máy (Phương án C) — status='archived' + TOTP L1 + confirm username
  adminHardDeleteUser: async ({ userId, usernameConfirm, totpCode }) => {
    const store = useAuthStore.getState();
    const headers = {
      'Content-Type': 'application/json',
      'X-TOTP-Code': totpCode,
    };
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    const res = await fetch(`${API_BASE}/admin/users/${userId}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ username_confirm: usernameConfirm }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    return data;
  },

  // Admin shops (M2R.4)
  adminShops:          ()            => apiJson('/admin/shops'),
  adminCreateShop:     (body)        => apiJson('/admin/shops',              { method: 'POST', body }),
  adminUpdateShop:     (id, body)    => apiJson(`/admin/shops/${id}`,        { method: 'PATCH', body }),
  adminDisableShop:    (id)          => apiJson(`/admin/shops/${id}/disable`, { method: 'POST' }),
  adminEnableShop:     (id)          => apiJson(`/admin/shops/${id}/enable`,  { method: 'POST' }),

  // Admin employees (M2R.4)
  adminEmployees:        (shopId)      => {
    const qs = shopId ? `?shop_id=${encodeURIComponent(shopId)}` : '';
    return apiJson(`/admin/employees${qs}`);
  },
  adminCreateEmployee:   (body)        => apiJson('/admin/employees',                 { method: 'POST', body }),
  adminUpdateEmployee:   (id, body)    => apiJson(`/admin/employees/${id}`,           { method: 'PATCH', body }),
  adminResetEmployeeL2:  (id)          => apiJson(`/admin/employees/${id}/reset-l2`,  { method: 'POST' }),
  adminUnlockEmployee:   (id)          => apiJson(`/admin/employees/${id}/unlock`,    { method: 'POST' }),
  adminDisableEmployee:  (id)          => apiJson(`/admin/employees/${id}/disable`,   { method: 'POST' }),
  adminEnableEmployee:   (id)          => apiJson(`/admin/employees/${id}/enable`,    { method: 'POST' }),

  // M2R.6+ Hard delete (Phương án C) — yêu cầu NV phải disabled + TOTP L1 + confirm họ tên
  adminHardDeleteEmployee: async ({ employeeId, fullNameConfirm, totpCode }) => {
    const store = useAuthStore.getState();
    const headers = {
      'Content-Type': 'application/json',
      'X-TOTP-Code': totpCode,
    };
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    const res = await fetch(`${API_BASE}/admin/employees/${employeeId}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ full_name_confirm: fullNameConfirm }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    return data;
  },

  // Questions (M3)
  questionsList: (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    return apiJson(`/questions${s ? '?' + s : ''}`);
  },
  questionGet:         (id)        => apiJson(`/questions/${id}`),
  questionCreate:      (body)      => apiJson(`/questions`,                      { method: 'POST',   body }),
  questionUpdate:      (id, body)  => apiJson(`/questions/${id}`,                { method: 'PATCH',  body }),
  questionDelete:      (id)        => apiJson(`/questions/${id}`,                { method: 'DELETE' }),
  // M29.A2 (2026-04-24) — AI scan cặp câu trùng cosine ≥ threshold (PRD R-P0-B4)
  questionsDuplicates: ({ bo, threshold = 0.88, limit = 20 } = {}) => {
    const qs = new URLSearchParams();
    qs.set('bo', bo);
    qs.set('threshold', String(threshold));
    qs.set('limit', String(limit));
    return apiJson(`/questions/duplicates?${qs.toString()}`);
  },
  // M30.A (2026-04-24) — RAG sinh câu hỏi mới (PRD R-P0-B4a)
  questionsAiGenerate: (body) =>
    apiJson('/questions/ai-generate', { method: 'POST', body }),
  // M14 — Bulk delete: xoá tất cả câu trong 1 bộ (cần TOTP admin L1).
  // Tính năng scope=all đã gỡ 2026-04-23 vì quá nguy hiểm.
  questionsBulkDelete: ({ bo, totpCode }) =>
    apiJson(`/questions/bulk?scope=bo&bo=${encodeURIComponent(bo)}`, { method: 'DELETE', adminTotp: totpCode }),
  questionMoveBo:      (id, bo)    => apiJson(`/questions/${id}/move-bo`,        { method: 'POST',   body: { bo } }),
  questionApprove:     (id)        => apiJson(`/questions/${id}/approve`,        { method: 'POST' }),
  questionApproveEdit: (id, body)  => apiJson(`/questions/${id}/approve-edit`,   { method: 'POST',   body }),
  questionReject:      (id, reason) => apiJson(`/questions/${id}/reject`,        { method: 'POST',   body: { reason } }),
  // 2026-04-24: Bulk action — body { ids[], action: 'approve'|'reject'|'delete'|'move_bo', target_bo? }
  questionsBulkAction: (body)       => apiJson('/questions/bulk-action',          { method: 'POST',   body }),

  // Topic order (M3 read + M3.5 reorder)
  topicsList:    (bo)        => apiJson(`/question-topics/${bo}`),
  topicsReorder: (bo, order) => apiJson(`/question-topics/${bo}/order`,           { method: 'PATCH', body: { order } }),

  // M137 (2026-05-10) — Admin exam settings + oversight
  adminExamSettings:        ()        => apiJson('/admin/exam-settings'),
  adminExamSettingsUpdate:  (body)    => apiJson('/admin/exam-settings', { method: 'PUT', body }),
  adminExamAttempts:        (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    return apiJson(`/admin/exam-attempts${s ? '?' + s : ''}`);
  },
  adminExamAttemptDetail:   (id)      => apiJson(`/admin/exam-attempts/${id}`),

  // Import/Export Excel (M3.5) - requires X-TOTP-Code (admin L2 OTP, PRD R-P0-B5)
  questionsImport: async (bo, file, totpCode) => {
    const store = useAuthStore.getState();
    const fd = new FormData();
    fd.append('file', file, file.name || 'import.xlsx');
    const headers = { 'X-TOTP-Code': totpCode };
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    const res = await fetch(`${API_BASE}/questions/import?bo=${encodeURIComponent(bo)}`, {
      method: 'POST',
      headers,
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    return data;
  },

  questionsExport: async (bo, totpCode) => {
    const store = useAuthStore.getState();
    const headers = { 'X-TOTP-Code': totpCode };
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    const res = await fetch(`${API_BASE}/questions/export?bo=${encodeURIComponent(bo)}`, {
      method: 'GET',
      headers,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    }
    const blob = await res.blob();
    const contentDisp = res.headers.get('Content-Disposition') || '';
    const match = /filename="([^"]+)"/.exec(contentDisp);
    const filename = match?.[1] || `${bo}_export.xlsx`;
    return { blob, filename };
  },

  // Flashcard (M6) - kiosk routes: authenticate + requireL2 → X-L2-Session header bắt buộc.
  // M194 cleanup (2026-05-24): 4 functions flashcardSession* removed (dead — BE routes deleted).
  // 2026-04-24: kind='question' (mặc định) hoặc 'rules' (ôn nội quy từ rules_test_questions)
  flashcardReview:    (cardId, q, kind = 'question') => apiJson('/flashcard/review', { method: 'POST', body: { cardId, quality: q, kind }, withL2: true }),
  flashcardReset:     (cardId)      => apiJson(`/flashcard/${cardId}/reset`,   { method: 'POST', withL2: true }),
  flashcardBrowse:    (setName, topic)  => {
    const qs = new URLSearchParams({ set: setName });
    if (topic) qs.set('topic', topic);
    return apiJson(`/flashcard/browse?${qs.toString()}`, { withL2: true });
  },
  flashcardProgress:  ()            => apiJson('/flashcard/progress', { withL2: true }),
  flashcardNext:      (mode = 'auto') => apiJson(
    `/flashcard/next-card${mode && mode !== 'auto' ? `?mode=${encodeURIComponent(mode)}` : ''}`,
    { withL2: true }
  ),

  // 2026-04-24: SM-2 parameters — admin điều chỉnh
  adminSm2Config:        ()    => apiJson('/admin/flashcard/sm2-config'),
  adminSm2ConfigUpdate:  (body) => apiJson('/admin/flashcard/sm2-config', { method: 'PUT', body }),

  // Admin flashcard (M6.5) - reset topic, requires X-TOTP-Code (admin L1)
  // M2 refactor: target là employeeId (NV thật), không phải userId.
  adminFlashcardResetTopic: async ({ employeeId, setName, topic, totpCode }) => {
    const store = useAuthStore.getState();
    const headers = {
      'Content-Type': 'application/json',
      'X-TOTP-Code': totpCode,
    };
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    const res = await fetch(`${API_BASE}/admin/flashcard/reset-topic`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ employeeId, setName, topic: topic ?? null }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    return data;
  },

  // Admin flashcard settings (quota / daily target) — admin login only, no TOTP
  adminFlashcardGetSettings:    ()             => apiJson('/admin/flashcard/settings'),
  adminFlashcardUpdateSettings: (payload)      => apiJson('/admin/flashcard/settings', { method: 'PUT', body: payload }),

  // ============================================================
  // Exam (M7) — kiosk routes: authenticate + requireL2 → withL2: true
  // PRD §5.4 + §6.1.E + §7.9 + Systemdesign §9.6.
  // ============================================================
  examAvailableSets: ()        => apiJson('/exam/available-sets', { withL2: true }),
  examQuota:         ()        => apiJson('/exam/quota',          { withL2: true }),
  // M316 (2026-05-27): NV xem lịch sử attempts cũ của 1 bộ (hoặc all)
  examHistory:       (bo = null) => apiJson(`/exam/history${bo ? '?bo=' + encodeURIComponent(bo) : ''}`, { withL2: true }),

  // M7.2a core flow
  examStart:        (bo)       => apiJson('/exam/start',          { method: 'POST', body: { bo }, withL2: true }),
  examAttempt:      (id)       => apiJson(`/exam/attempt/${id}`,  { withL2: true }),
  examAnswer:       (body)     => apiJson('/exam/answer',         { method: 'POST', body, withL2: true }),
  examSubmit:       (attemptId) => apiJson('/exam/submit',        { method: 'POST', body: { attemptId }, withL2: true }),
  examPasteBlocked: (body)     => apiJson('/exam/paste-blocked',  { method: 'POST', body, withL2: true }),

  // M7.3c — result endpoint (after LLM grading)
  examAttemptResult: (id)      => apiJson(`/exam/attempt/${id}/result`, { withL2: true }),

  // M121 (2026-04-27) — chấm 1 bộ nhỏ giữa phiên (attempt vẫn in_progress)
  examGradeChunk:   (attemptId, chunkIndex) =>
    apiJson(`/exam/attempt/${attemptId}/grade-chunk`, {
      method: 'POST', body: { chunkIndex }, withL2: true,
    }),

  // M139 (2026-05-10) — NV xem lại các bộ nhỏ đã chấm trong phiên
  examGradedChunks: (attemptId) =>
    apiJson(`/exam/attempt/${attemptId}/graded-chunks`, { withL2: true }),

  // M150 (2026-05-13) — heartbeat active-time. seconds tối đa 15/lần.
  // `keepalive: true` → tab đóng vẫn gửi xong request (modern thay sendBeacon).
  examHeartbeat: (attemptId, seconds) =>
    apiJson(`/exam/attempt/${attemptId}/heartbeat`, {
      method: 'POST', body: { seconds }, withL2: true, keepalive: true,
    }),

  // ============================================================
  // Ask AI (M8) — kiosk routes: authenticate + requireL2 → withL2: true
  // PRD §5.5 + §6.1.F + §7.10 + Systemdesign §10.
  // ============================================================
  askStep1: (question, bo)     => apiJson('/ask/step1', { method: 'POST', body: { question, bo }, withL2: true }),
  askStep2: (body)             => apiJson('/ask/step2', { method: 'POST', body, withL2: true }),
  // BE route FeedbackBody: { feedback: 'up'|'down' }
  askFeedback: (historyId, value) =>
    apiJson(`/ask/${historyId}/feedback`, { method: 'POST', body: { feedback: value }, withL2: true }),
  // BE route ClickBody: { candidate_id: int }
  askClick: (historyId, candidateId) =>
    apiJson(`/ask/${historyId}/click`,    { method: 'POST', body: { candidate_id: candidateId }, withL2: true }),

  // Bookmarks (M8.4-fix-B) — BE PostBody: { question_id: int, note?: string }
  bookmarkList:    ()          => apiJson('/bookmarks', { withL2: true }),
  bookmarkAdd:     (questionId) => apiJson('/bookmarks', { method: 'POST', body: { question_id: questionId }, withL2: true }),
  bookmarkRemove:  (questionId) => apiJson(`/bookmarks/${questionId}`, { method: 'DELETE', withL2: true }),

  // ============================================================
  // Admin AI providers + routing (M5.4) — admin login only
  // ============================================================
  adminAiProviders:       ()                  => apiJson('/admin/ai/providers'),
  // M78c (2026-04-25): chấp nhận opts.adminTotp để FE pass mã TOTP khi đổi api_key
  // (backend yêu cầu X-Admin-TOTP header — xem admin-ai.js line 289-302)
  adminAiUpdateProvider:  (provider, body, opts = {})    => apiJson(`/admin/ai/providers/${provider}`, { method: 'PUT', body, adminTotp: opts.adminTotp }),
  adminAiTestProvider:    (provider)          => apiJson(`/admin/ai/providers/${provider}/test`, { method: 'POST' }),
  // M19 (2026-04-24) — refresh list models
  adminAiRefreshModels:   (provider)          => apiJson(`/admin/ai/providers/${provider}/refresh-models`, { method: 'POST', body: {} }),
  adminAiRouting:         ()                  => apiJson('/admin/ai/routing'),
  adminAiUpdateRouting:   (task, body)        => apiJson(`/admin/ai/routing/${task}`, { method: 'PUT', body }),

  // ============================================================
  // M49 (2026-04-25) — Admin AI Scope: bật/tắt vùng dữ liệu AI truy cập
  // ============================================================
  adminAiScope:           ()                  => apiJson('/admin/ai-scope'),
  adminAiScopeUpdate:     (body)              => apiJson('/admin/ai-scope', { method: 'PATCH', body }),

  // ============================================================
  // M50 (2026-04-25) — Admin AI Tuning: tham số + preset + model recommendations
  // ============================================================
  adminAiTuning:          ()                  => apiJson('/admin/ai-tuning'),
  adminAiTuningUpdate:    (body)              => apiJson('/admin/ai-tuning', { method: 'PATCH', body }),
  adminAiTuningPreset:    (preset_id)         => apiJson('/admin/ai-tuning/apply-preset', { method: 'POST', body: { preset_id } }),
  // M112 (2026-04-26): User profiles
  adminAiTuningProfiles:        ()        => apiJson('/admin/ai-tuning/profiles'),
  adminAiTuningProfileCreate:   (body)    => apiJson('/admin/ai-tuning/profiles', { method: 'POST', body }),
  adminAiTuningProfileUpdate:   (id, body) => apiJson(`/admin/ai-tuning/profiles/${id}`, { method: 'PATCH', body }),
  adminAiTuningProfileDelete:   (id)      => apiJson(`/admin/ai-tuning/profiles/${id}`, { method: 'DELETE' }),
  adminAiTuningProfileApply:    (id)      => apiJson(`/admin/ai-tuning/profiles/${id}/apply`, { method: 'POST', body: {} }),

  // ============================================================
  // M95 (2026-04-26) — Admin AI Pricing: bảng giá model + sync online
  // ============================================================
  adminAiPricing:                 ()         => apiJson('/admin/ai/pricing'),
  adminAiPricingCreate:           (body)     => apiJson('/admin/ai/pricing', { method: 'POST', body }),
  adminAiPricingUpdate:           (id, body) => apiJson(`/admin/ai/pricing/${id}`, { method: 'PUT', body }),
  adminAiPricingDelete:           (id)       => apiJson(`/admin/ai/pricing/${id}`, { method: 'DELETE' }),
  adminAiPricingSyncLitellmPreview: ()       => apiJson('/admin/ai/pricing/sync-litellm/preview', { method: 'POST', body: {} }),
  adminAiPricingSyncLitellmApply:   ()       => apiJson('/admin/ai/pricing/sync-litellm/apply',   { method: 'POST', body: {} }),
  adminAiPricingSyncVcbPreview:     ()       => apiJson('/admin/ai/pricing/sync-vcb/preview',     { method: 'POST', body: {} }),
  adminAiPricingSyncVcbApply:       ()       => apiJson('/admin/ai/pricing/sync-vcb/apply',       { method: 'POST', body: {} }),

  // ============================================================
  // Admin embedding (M23 — gọn lại, chỉ còn badge + retry)
  // CŨ (M5.4.6): adminEmbeddingStatus/Backfill/Rebuild đã BỊ GỠ 2026-04-24
  // vì workflow chuyển sang "auto-embed khi create/update/import".
  // MỚI: chỉ còn 2 endpoint phục vụ badge cảnh báo + nút retry khi auto fail.
  // ============================================================
  adminQuestionsMissingEmbedCount: () =>
    apiJson('/embedding/questions/missing-count'),
  adminQuestionsRetryMissingEmbed: () =>
    apiJson('/embedding/questions/retry-missing', { method: 'POST', body: {} }),
  // M44 (2026-04-24): Rebuild all embedding khi admin đổi model
  adminEmbedRebuildAllStatus: () =>
    apiJson('/embedding/rebuild-all/status'),
  adminEmbedRebuildAll: (adminTotp) =>
    apiJson('/embedding/rebuild-all', { method: 'POST', body: {}, adminTotp }),

  // ============================================================
  // Rules / Nội quy (M9 → M80) — M170 cleanup 2026-05-15
  //   - AdminNoiQuyPage gỡ — thay bằng /admin/rules-modules + /admin/rules-m80.
  //   - rulesVersions giữ vì AdminRulesModulesPage gọi với `?.()` optional fallback.
  //   - rulesExamStart/Attempt/Submit/History đã gỡ (M9 rules exam phased out by M80).
  //   - Toàn bộ FE M9 admin (rulesCurrent/Import/Commit/Edit/Rollback/Diff/EmbedChunks/
  //     ChunksStatus/ChunksPurge/PreviewDiscard/VersionGet/Ack/AckStatus/Tq*) đã gỡ.
  //   - BE routes/rules.js + rulesService giữ cho rulesEmbedService internal dùng
  //     getCurrentVersion + tests rules-m9.test.js đang pass. Phase 7.7 cleanup BE sau.
  // ============================================================
  rulesVersions:       ({ limit } = {}) =>
    apiJson(`/rules/versions${limit ? `?limit=${limit}` : ''}`),

  // ============================================================
  // Chat AI admin (M10.5/M10.6/M10.7) — admin login only
  //   PRD §6.1.H R-P0-H1..H8 + §7.3 AC-CHAT-1..4
  //   - sessions CRUD: gate admin
  //   - send message: gate admin (call LLM + RAG)
  //   - save "dặn dò": convert AI message → prompt row
  //   - capabilities: check supports_multimodal để FE ẩn/hiện upload
  // ============================================================
  chatAiCapabilities:  ()                 => apiJson('/admin/chat-ai/capabilities'),
  chatAiListSessions:  ()                 => apiJson('/admin/chat-ai/sessions'),
  chatAiCreateSession: (body = {})        => apiJson('/admin/chat-ai/sessions', { method: 'POST', body }),
  chatAiGetSession:    (id)               => apiJson(`/admin/chat-ai/sessions/${id}`),
  chatAiRenameSession: (id, title)        =>
    apiJson(`/admin/chat-ai/sessions/${id}`, { method: 'PATCH', body: { title } }),
  chatAiDeleteSession: (id)               =>
    apiJson(`/admin/chat-ai/sessions/${id}`, { method: 'DELETE' }),
  chatAiSendMessage:   (id, body)         =>
    apiJson(`/admin/chat-ai/sessions/${id}/messages`, { method: 'POST', body }),
  chatAiSaveDanDo:     (body)             =>
    apiJson('/admin/chat-ai/save-dan-do', { method: 'POST', body }),

  // ============================================================
  // Admin Knowledge Base (M10.5) — admin login + embed TOTP re-verify
  //   BE: listKb({q, tag, limit, offset}) — KHÔNG có active/toggle
  // ============================================================
  kbList:              ({ q, tag, limit, offset } = {}) => {
    const qs = new URLSearchParams();
    if (q)      qs.set('q', q);
    if (tag)    qs.set('tag', tag);
    if (limit)  qs.set('limit', String(limit));
    if (offset) qs.set('offset', String(offset));
    const s = qs.toString();
    return apiJson(`/admin/knowledge-base${s ? '?' + s : ''}`);
  },
  kbGet:               (id)               => apiJson(`/admin/knowledge-base/${id}`),
  kbCreate:            (body)             =>
    apiJson('/admin/knowledge-base', { method: 'POST', body }),
  kbUpdate:            (id, body)         =>
    apiJson(`/admin/knowledge-base/${id}`, { method: 'PATCH', body }),
  kbDelete:            (id)               =>
    apiJson(`/admin/knowledge-base/${id}`, { method: 'DELETE' }),
  kbEmbed:             (id, body, { adminTotp } = {}) =>
    apiJson(`/admin/knowledge-base/${id}/embed`, { method: 'POST', body: body || {}, adminTotp }),
  // M17 — Embed đồng loạt KB chưa embed
  kbEmbedMissing:      ()                  =>
    apiJson('/admin/knowledge-base/embed-missing', { method: 'POST', body: {} }),
  // M100 (2026-04-26) — Re-embed TẤT CẢ KB (sau khi đổi chunk strategy/max_words)
  kbReembedAll:        ({ adminTotp } = {}) =>
    apiJson('/admin/knowledge-base/reembed-all', { method: 'POST', body: {}, adminTotp }),
  // M157 (2026-05-13) — Tải toàn bộ KB ra file Excel. Cost-sensitive → X-Admin-TOTP.
  // M157b (2026-05-13): đổi từ JSON sang XLSX để admin chỉnh sửa hàng loạt trong Excel.
  kbExportAll: async ({ adminTotp } = {}) => {
    const store = useAuthStore.getState();
    const headers = {};
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    if (adminTotp) headers['X-Admin-TOTP'] = String(adminTotp).trim();
    const res = await fetch(`${API_BASE}/admin/knowledge-base/export-all`, { method: 'GET', headers });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    }
    const blob = await res.blob();
    const contentDisp = res.headers.get('Content-Disposition') || '';
    const match = /filename="([^"]+)"/.exec(contentDisp);
    const filename = match?.[1] || 'knowledge-base-export.xlsx';
    return { blob, filename };
  },
  // M157b (2026-05-13) — Import hàng loạt KB từ Excel. Cost-sensitive → X-Admin-TOTP.
  kbImportExcel: async (file, adminTotp) => {
    const store = useAuthStore.getState();
    const fd = new FormData();
    fd.append('file', file, file.name || 'import.xlsx');
    const headers = {};
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    if (adminTotp) headers['X-Admin-TOTP'] = String(adminTotp).trim();
    const res = await fetch(`${API_BASE}/admin/knowledge-base/import-excel`, {
      method: 'POST',
      headers,
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    return data;
  },
  // M20 (2026-04-24) — Upload file .md/.txt/.docx → convert sang markdown
  kbImportFile: async (file) => {
    const store = useAuthStore.getState();
    const fd = new FormData();
    fd.append('file', file, file.name || 'upload');
    const headers = {};
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    const res = await fetch(`${API_BASE}/admin/knowledge-base/import-file`, {
      method: 'POST',
      headers,
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    return data;
  },

  // ============================================================
  // Admin Prompts (M10.5) — admin login + embed TOTP re-verify
  //   scope: 'ask_ai' | 'chat_ai' | 'both'
  // ============================================================
  promptsList:         ({ scope, activeOnly, q } = {}) => {
    const qs = new URLSearchParams();
    if (scope)       qs.set('scope', scope);
    if (activeOnly)  qs.set('activeOnly', '1');
    if (q)           qs.set('q', q);
    const s = qs.toString();
    return apiJson(`/admin/prompts${s ? '?' + s : ''}`);
  },
  promptsGet:          (id)               => apiJson(`/admin/prompts/${id}`),
  promptsCreate:       (body)             =>
    apiJson('/admin/prompts', { method: 'POST', body }),
  promptsUpdate:       (id, body)         =>
    apiJson(`/admin/prompts/${id}`, { method: 'PATCH', body }),
  promptsDelete:       (id)               =>
    apiJson(`/admin/prompts/${id}`, { method: 'DELETE' }),
  promptsToggle:       (id, active)       =>
    apiJson(`/admin/prompts/${id}/toggle`, { method: 'POST', body: { active } }),
  promptsEmbed:        (id, { adminTotp } = {}) =>
    apiJson(`/admin/prompts/${id}/embed`, { method: 'POST', body: {}, adminTotp }),
  // M17 — Embed đồng loạt prompts chưa embed
  promptsEmbedMissing: ()                  =>
    apiJson('/admin/prompts/embed-missing', { method: 'POST', body: {} }),
  // M157 (2026-05-13) — Tải toàn bộ prompts ra file Excel. Cost-sensitive → X-Admin-TOTP.
  // M157b (2026-05-13): đổi từ JSON sang XLSX.
  promptsExportAll: async ({ adminTotp } = {}) => {
    const store = useAuthStore.getState();
    const headers = {};
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    if (adminTotp) headers['X-Admin-TOTP'] = String(adminTotp).trim();
    const res = await fetch(`${API_BASE}/admin/prompts/export-all`, { method: 'GET', headers });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    }
    const blob = await res.blob();
    const contentDisp = res.headers.get('Content-Disposition') || '';
    const match = /filename="([^"]+)"/.exec(contentDisp);
    const filename = match?.[1] || 'prompts-export.xlsx';
    return { blob, filename };
  },
  // M157b (2026-05-13) — Import hàng loạt prompts từ Excel. Cost-sensitive → X-Admin-TOTP.
  promptsImportExcel: async (file, adminTotp) => {
    const store = useAuthStore.getState();
    const fd = new FormData();
    fd.append('file', file, file.name || 'import.xlsx');
    const headers = {};
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    if (adminTotp) headers['X-Admin-TOTP'] = String(adminTotp).trim();
    const res = await fetch(`${API_BASE}/admin/prompts/import-excel`, {
      method: 'POST',
      headers,
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    return data;
  },

  // ============================================================
  // Admin Telegram (M11.A) — admin login only
  //   Routes trong apps/backend/src/routes/telegram.js
  //   - Config: {enabled, has_token, token_preview, webhook_url, is_operational}
  //   - Recipients: {chat_id, label, user_id, notify_ai_ask/login_fail/budget_alert/weekly_report/milestones}
  // ============================================================
  telegramConfigGet:     ()           => apiJson('/telegram/config'),
  telegramConfigUpdate:  (body)       => apiJson('/telegram/config',      { method: 'PUT',  body }),
  telegramConfigTest:    (chat_id)    => apiJson('/telegram/config/test', { method: 'POST', body: { chat_id } }),

  telegramRecipientsList:   ()        => apiJson('/telegram/recipients'),
  telegramRecipientCreate:  (body)    => apiJson('/telegram/recipients',        { method: 'POST',   body }),
  telegramRecipientUpdate:  (id, body) => apiJson(`/telegram/recipients/${id}`, { method: 'PATCH',  body }),
  telegramRecipientDelete:  (id)      => apiJson(`/telegram/recipients/${id}`,  { method: 'DELETE' }),

  // M319 — infra monitoring (cron alert backup PG > 48h)
  telegramInfraMonitoringGet:    ()         => apiJson('/telegram/infra-monitoring'),
  telegramInfraMonitoringToggle: (enabled)  => apiJson('/telegram/infra-monitoring', { method: 'PUT', body: { enabled } }),

  // ============================================================
  // Admin Motivation copies (M11.C) — PRD R-P0-O9
  //   settings_kv key='motivation.copy.json'
  //   6 event key: exam_pass, exam_fail, rules_exam_pass,
  //     flashcard_session_done, streak_milestone, reward_unlock
  // ============================================================
  motivationGet:      ()          => apiJson('/admin/motivation/copies'),
  motivationUpdate:   (body)      => apiJson('/admin/motivation/copies',        { method: 'PUT',  body }),
  motivationReset:    ()          => apiJson('/admin/motivation/copies/reset',  { method: 'POST', body: {} }),
  motivationPreview:  (event, vars) => apiJson('/admin/motivation/preview',     { method: 'POST', body: { event, vars } }),

  // ============================================================
  // Admin Progress (M12.A) — PRD §7.6 AC-PROG-1..4
  //   Routes trong apps/backend/src/routes/admin-progress.js
  //   - overview:   { employees: [{id, name, shop, last_active, streak,
  //                   questions_7d, questions_30d, exam_pass_rate,
  //                   flashcard_sessions_7d, avg_accuracy, cost_30d_vnd,
  //                   flags: {inactive_3d, low_accuracy, high_cost}}] }
  //   - employee detail: { employee, kpi, exams[], flashcards[], qa[], cost_series[] }
  //   - leaderboard: { period, rows: [{id, name, shop, points, rank}] }
  //   - alerts:      { items: [{employee_id, name, alert_type, value, message}] }
  // ============================================================
  progressOverview:        ()              => apiJson('/admin/progress/overview'),
  progressEmployeeDetail:  (id)            => apiJson(`/admin/progress/employee/${id}`),
  progressLeaderboard:     (period = 'month') => apiJson(`/admin/progress/leaderboard?period=${encodeURIComponent(period)}`),
  progressAlerts:          ()              => apiJson('/admin/progress/alerts'),
  progressRemind:          (body)          => apiJson('/admin/progress/alerts/remind', { method: 'POST', body }),
  // 2026-04-24: Dashboard feedback Đúng/Sai toàn shop (admin xem AI ẩu chỗ nào)
  progressAiFeedback:      (days = 30)     => apiJson(`/admin/progress/ai-feedback?days=${encodeURIComponent(days)}`),

  // M330 (2026-08-09): Theo dõi ôn tập từng câu — lịch sử bấm (M329 log) + thống kê Quên/Khó
  flashcardTrackingEmployee: (id, { bo, q } = {}) => {
    const params = new URLSearchParams();
    if (bo) params.set('bo', bo);
    if (q) params.set('q', q);
    const qs = params.toString();
    return apiJson(`/admin/flashcard-tracking/employee/${id}${qs ? `?${qs}` : ''}`);
  },
  flashcardTrackingStats: ({ days = '30', bo } = {}) => {
    const params = new URLSearchParams({ days });
    if (bo) params.set('bo', bo);
    return apiJson(`/admin/flashcard-tracking/stats?${params.toString()}`);
  },

  // 2026-04-24: Rewards — NV xem thưởng + admin quản lý chi trả
  rewardsMe:               ()              => apiJson('/rewards/me'),
  adminRewardsList:        (filter = 'unpaid') => apiJson(`/admin/rewards?filter=${encodeURIComponent(filter)}`),
  adminRewardMarkPaid:     (id, note)      => apiJson(`/admin/rewards/${id}/mark-paid`, { method: 'POST', body: { note: note || null } }),
  adminRewardUnmarkPaid:   (id)            => apiJson(`/admin/rewards/${id}/unmark-paid`, { method: 'POST', body: {} }),

  // 2026-04-24: Rules Modules (NV)
  rulesModulesList:        ()              => apiJson('/rules/modules', { withL2: true }),
  rulesModuleGet:          (id)            => apiJson(`/rules/modules/${id}`, { withL2: true }),
  rulesModuleCheckAnswer:  (mid, qid, ans) => apiJson(`/rules/modules/${mid}/questions/${qid}/check`, { method: 'POST', body: { answer: ans }, withL2: true }),
  // M196 cleanup: rulesModuleRate removed (dead — RuleModulePage M35 không dùng nữa)

  // 2026-04-26: M80 — học theo bộ AI sinh on-demand + cơ chế phao
  rulesM80SetCreate:       (moduleId, isReview = false) =>
    apiJson('/rules/sets', { method: 'POST', body: { module_id: moduleId, is_review: !!isReview }, withL2: true }),
  rulesM80SetGet:          (setId) => apiJson(`/rules/sets/${setId}`, { withL2: true }),
  rulesM80SetActive:       (moduleId) => apiJson(`/rules/sets/active?module_id=${moduleId}`, { withL2: true }),
  rulesM80SetPeek:         (setId, currentAttemptId = null) =>
    apiJson(`/rules/sets/${setId}/peek`, { method: 'POST', body: { current_attempt_id: currentAttemptId }, withL2: true }),
  rulesM80SetComplete:     (setId) =>
    apiJson(`/rules/sets/${setId}/complete`, { method: 'POST', body: {}, withL2: true }),
  rulesM80AnswerSubmit:    (attemptId, answerText) =>
    apiJson(`/rules/attempts/${attemptId}/answer`, { method: 'POST', body: { answer_text: answerText }, withL2: true }),
  rulesM80RoundCurrent:    () => apiJson('/rules/round/current', { withL2: true }),
  rulesM80RoundFinishAll:  () => apiJson('/rules/round/finish-all-chapters', { method: 'POST', body: {}, withL2: true }),
  meDailyTasks:            () => apiJson('/me/daily-tasks', { withL2: true }),

  // M87 (2026-04-26): admin sắp xếp topic order cho 3 bộ thưởng (flashcard learn)
  adminFlashcardTopicOrderGet: (bo) => apiJson(`/admin/flashcard/topic-order?bo=${encodeURIComponent(bo)}`),
  adminFlashcardTopicOrderSet: (bo, ordered_topics) =>
    apiJson('/admin/flashcard/topic-order', { method: 'PUT', body: { bo, ordered_topics } }),

  // M80 Admin
  adminRulesM80TodayStats:        (date) => apiJson(`/admin/rules/today-stats${date ? `?date=${date}` : ''}`),
  adminRulesM80EmployeesSummary:  (date) => apiJson(`/admin/rules/employees-summary${date ? `?date=${date}` : ''}`),
  adminRulesM80EmployeeSessions:  (employeeId, { from, to, module_id } = {}) => {
    const qs = new URLSearchParams();
    if (from)      qs.set('from', from);
    if (to)        qs.set('to', to);
    if (module_id) qs.set('module_id', module_id);
    const q = qs.toString();
    return apiJson(`/admin/rules/employee/${employeeId}/sessions${q ? `?${q}` : ''}`);
  },
  adminRulesM80SetDetails:        (setId) => apiJson(`/admin/rules/sets/${setId}/details`),
  adminRulesM80SettingsGet:       () => apiJson('/admin/rules/settings'),
  adminRulesM80SettingsUpdate:    (body) => apiJson('/admin/rules/settings', { method: 'PATCH', body }),

  // 2026-04-24: Rules Modules (Admin)
  adminRulesModulesList:    (vid)             => apiJson(`/admin/rules/versions/${vid}/modules`),
  adminRulesModuleCreate:   (vid, body)       => apiJson(`/admin/rules/versions/${vid}/modules`, { method: 'POST', body }),
  // M187 cleanup: gỡ adminRulesModuleAiGenQuestions (BE route dead, FE không call)
  // M71 (2026-04-25) — log NV cố tình copy/paste khi làm bài kiểm tra nội quy
  rulesLogCheatAttempt:     (body)  => apiJson('/rules/log-cheat-attempt', { method: 'POST', body }),
  // M72 — admin xem cheat log
  adminRulesCheatLog: ({ from, to, employee_id, action, limit, offset } = {}) => {
    const qs = new URLSearchParams();
    if (from)        qs.set('from', from);
    if (to)          qs.set('to', to);
    if (employee_id) qs.set('employee_id', employee_id);
    if (action)      qs.set('action', action);
    if (limit)       qs.set('limit', limit);
    if (offset)      qs.set('offset', offset);
    const s = qs.toString();
    return apiJson('/admin/rules/cheat-log' + (s ? `?${s}` : ''));
  },
  adminCheatLogMethods: () => apiJson('/admin/rules/cheat-log/methods'),  // M320
  adminExamAttemptResetDevice: (attemptId, reason) =>                       // M320
    apiJson(`/admin/exam-attempts/${attemptId}/reset-device`, { method: 'POST', body: { reason } }),

  // M323 (2026-05-30) — Thông báo popup cho NV
  getAnnouncement:       () => apiJson('/announcement'),
  adminGetAnnouncement:  () => apiJson('/admin/announcement'),
  adminSaveAnnouncement: (data) => apiJson('/admin/announcement', { method: 'PUT', body: data }),

  // M57 (2026-04-25) — Import docx → tự tách chương theo H1
  adminRulesModulesImportDocx: async (vid, file) => {
    const store = useAuthStore.getState();
    const fd = new FormData();
    fd.append('file', file, file.name || 'upload.docx');
    const headers = {};
    if (store.accessToken) headers['Authorization'] = `Bearer ${store.accessToken}`;
    const res = await fetch(`${API_BASE}/admin/rules/versions/${vid}/modules/import-docx`, {
      method: 'POST',
      headers,
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || `HTTP ${res.status}`), { status: res.status, data });
    return data;
  },
  adminRulesModuleGet:      (id)              => apiJson(`/admin/rules/modules/${id}`),
  adminRulesModuleUpdate:   (id, body)        => apiJson(`/admin/rules/modules/${id}`, { method: 'PATCH', body }),
  adminRulesModuleDelete:   (id)              => apiJson(`/admin/rules/modules/${id}`, { method: 'DELETE' }),
  adminRulesModulesReorder: (vid, ordered_ids) => apiJson(`/admin/rules/versions/${vid}/modules/reorder`, { method: 'POST', body: { ordered_ids } }),
  // M187 cleanup: gỡ adminRulesQuestionAdd/Update/Delete/Reorder (BE routes dead, FE không call)
  // 2026-04-24: Xoá TẤT CẢ chương của version — cần TOTP admin reverify
  adminRulesModulesDeleteAll: (vid, adminTotp) =>
    apiJson(`/admin/rules/versions/${vid}/modules`, { method: 'DELETE', adminTotp }),

  // M43 (2026-04-24): Embed chương nội quy (question embed gỡ M187)
  adminRulesModuleEmbed:        (id)  => apiJson(`/admin/rules/modules/${id}/embed`, { method: 'POST' }),
  adminRulesEmbedMissingCount:  ()    => apiJson('/admin/rules/embed-missing-count'),
  adminRulesEmbedRetryMissing:  ()    => apiJson('/admin/rules/embed-retry-missing', { method: 'POST' }),
  // M104 (2026-04-26): Re-embed TẤT CẢ chương (re-chunk + re-embed) — TOTP guard
  adminRulesReembedAll:         ({ adminTotp } = {}) =>
    apiJson('/admin/rules/modules/reembed-all', { method: 'POST', body: {}, adminTotp }),

  // 2026-04-24: Feature flags
  getFeatureFlags:    ()              => apiJson('/settings/features'),
  setFeatureFlags:    (body)          => apiJson('/admin/settings/features', { method: 'PUT', body }),

  // 2026-04-24: Upload ảnh cho rich text editor nội quy — multipart
  // Không dùng raw() (nó auto-stringify JSON) → dùng fetch trực tiếp
  rulesUploadImage: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const store = useAuthStore.getState();
    const res = await fetch('/api/admin/rules/upload-image', {
      method: 'POST',
      headers: store.accessToken ? { 'Authorization': `Bearer ${store.accessToken}` } : {},
      body: fd,  // browser tự set Content-Type multipart boundary
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({ error: 'Upload fail' }));
      const err = new Error(errData.error || 'Upload fail');
      err.status = res.status; err.data = errData;
      throw err;
    }
    return res.json();
  },
  progressEmployeeExamHistoryPdf: async (id) => {
    const res = await raw(`/admin/progress/employee/${id}/exam-history.pdf`);
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Xuất PDF thất bại (${res.status}): ${txt.slice(0, 200)}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const cd = res.headers.get('content-disposition') || '';
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m ? m[1] : `lich-su-kiem-tra-${id}.pdf`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return { ok: true, filename };
  },

  // ============================================================
  // Admin Cost Dashboard (M12.B) — PRD §7.5 AC-DASH-1..3 + §6.1.K R-P0-K1..K8
  //   Routes trong apps/backend/src/routes/admin-cost.js (adminOnly, rate 60/min)
  //   - summary:      { today_vnd, week_vnd, month_vnd, budget_vnd, pct_of_budget, status }
  //   - chart:        { range: {from, to}, points: [{date, vnd}] } — zero-fill 30 ngày
  //   - top-users:    { period, rows: [{employee_id, name, shop, total_vnd, count}] }
  //   - split:        { range: {from, to}, rows: [{task, provider, total_vnd, count}] }
  //   - history:      { page, size, total, rows: [{id, ts, task, provider, model,
  //                     employee_id, employee_name, shop_code, cost_usd, cost_vnd,
  //                     prompt_tokens, completion_tokens, latency_ms, success, preview}] }
  // ============================================================
  costSummary:      ()                  => apiJson('/admin/cost/summary'),
  costChart:        ({ from, to } = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to)   qs.set('to', to);
    const s = qs.toString();
    return apiJson(`/admin/cost/chart${s ? '?' + s : ''}`);
  },
  // M28.A (2026-04-24): support today/yesterday preset + custom from/to.
  // Nếu truyền from+to thì BE dùng thẳng; nếu không thì theo period.
  costTopUsers:     ({ period = 'month', limit = 5, from, to } = {}) => {
    const qs = new URLSearchParams();
    qs.set('period', period);
    qs.set('limit', String(limit));
    if (from) qs.set('from', from);
    if (to)   qs.set('to', to);
    return apiJson(`/admin/cost/top-users?${qs.toString()}`);
  },
  costSplit:        ({ from, to } = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to)   qs.set('to', to);
    const s = qs.toString();
    return apiJson(`/admin/cost/split${s ? '?' + s : ''}`);
  },
  costHistory:      (params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    const s = qs.toString();
    return apiJson(`/admin/cost/history${s ? '?' + s : ''}`);
  },
};
